// Duenner Client fuer das Plant-Maintenance-Backend unter /api/pm.
// Portierung aus dem Vite-Prototyp (frontend/src/api.js): dort sprach ein eigener
// fetch-Wrapper BASE='/api/pm' an. Im Monolithen gilt dieselbe Auth-Konvention wie
// core/api/apiService (Bearer-JWT aus user.token, mock-Token wird uebersprungen)
// und dasselbe /api-Praefix.
//
// MONOLITH-KONVENTION: `user` ist das ERSTE Argument jeder Funktion und traegt
// den JWT (.token). Die Schreib-Operationen behalten den Idempotency-Key-Header
// aus dem Prototyp — ein Retry darf in SAP niemals ein Duplikat erzeugen.
//
// ALLE Aufrufe (auch Reads) laufen ueber pmCall statt core apiCall: einheitliche
// Fehlerbehandlung (Business-Failures als Rueckgabewert, geworfene Fehler tragen
// .status — OrderDetail unterscheidet damit 404 von "Backend nicht erreichbar")
// und optionale Zusatz-Header (Idempotency-Key, X-PM-Role).

/**
 * Interner Wrapper fuer Aufrufe MIT Zusatz-Headern (z. B. Idempotency-Key).
 * Verhaelt sich wie core apiCall() (/api-Praefix, JSON, Bearer-Auth nur wenn user.token existiert
 * und nicht mit 'mock-' beginnt) — mit EINEM bewussten Unterschied bei Fehlern:
 *   - Traegt der Fehler-Body die WriteResult-Form {ok:false, error} (Business-Failure des Adapters
 *     bzw. 403 des Funktionsrechte-Gates), wird der Body ZURUECKGEGEBEN, nicht geworfen. Die
 *     Screens verzweigen ohnehin auf res.ok und zeigen res.error — so sieht der Nutzer die echte
 *     SAP-/Backend-Meldung statt "HTTP error! status: 400".
 *   - Alles andere (500, Netzwerk, Nicht-JSON) wirft weiterhin, mit Backend-Message falls vorhanden.
 * @param {string} endpoint — Pfad NACH /api, z. B. '/pm/orders'
 * @param {string} method
 * @param {any} data — JSON-Body oder null
 * @param {Object} user — { token, ... }
 * @param {Object} [extraHeaders] — z. B. { 'Idempotency-Key': '...', 'X-PM-Role': 'mechanic' }
 */
/** Aktive UI-Sprache fuer den X-PM-Language-Header. Bewusst aus localStorage statt aus dem
 *  LanguageContext gelesen: api.js ist ein reines Modul ohne React-Kontext, und der Key ist
 *  dieselbe Quelle, aus der der Provider beim Start initialisiert (core/i18n/LanguageContext). */
const uiLanguage = () => {
  try {
    return localStorage.getItem('language') || (navigator.language || 'en').slice(0, 2);
  } catch {
    return 'en';
  }
};

const pmCall = async (endpoint, method = 'GET', data = null, user = null, extraHeaders = {}) => {
  const config = {
    method,
    headers: {
      'Content-Type': 'application/json',
      // SAP antwortet in der Sprache der SESSION, nicht in der der Oberflaeche: ohne diesen Header
      // bekommt eine englische UI die Meldungen des technischen SAP-Users (auf DEV deutsch —
      // "Equipment 10000001 nicht vorhanden"). Der Server reicht ihn als ctx.language weiter.
      'X-PM-Language': uiLanguage(),
      ...extraHeaders,
    },
  };
  if (user?.token && !user.token.startsWith('mock-')) {
    config.headers['Authorization'] = `Bearer ${user.token}`;
  }
  if (data) {
    config.body = JSON.stringify(data);
  }
  const response = await fetch(`/api${endpoint}`, config);
  if (!response.ok) {
    let body = null;
    try { body = await response.json(); } catch { /* kein JSON-Body -> generischer Fehler */ }
    if (body && body.ok === false) return body; // WriteResult-Business-Failure: Screens zeigen body.error
    const err = new Error(body?.error || body?.message || `HTTP error! status: ${response.status}`);
    err.status = response.status; // Screens unterscheiden 404 (fachlich) von Transport-/Serverfehlern
    throw err;
  }
  return response.json();
};

/** Aktive PM-Rolle als Header — auch fuer READS: das serverseitige Werks-Scope-Gate
 *  (routes.js plantScopeError/scopeRows) muss gegen DASSELBE Profil pruefen wie die UI-Rolle,
 *  sonst divergieren Reads (JWT-Rolle) und Writes (Umschalter-Rolle), sobald Profile
 *  unterschiedliche Werke tragen. */
const roleHeader = (role) => (role ? { 'X-PM-Role': role } : {});

/** Schreib-Header buendeln: Idempotency-Key + (falls bekannt) die aktive PM-Rolle fuer das
 *  serverseitige Funktionsrechte-Gate (routes.js requireRight). */
const writeHeaders = (idempotencyKey, role) => ({
  'Idempotency-Key': idempotencyKey,
  ...roleHeader(role),
});

// ==================== PLANT-MAINTENANCE-MODUL (/api/pm/*) ====================

/**
 * GET /pm/me?role=
 * Liefert das Demo-Profil/die Rechte fuer die gewaehlte Rolle.
 * @param {Object} user
 * @param {string} role
 */
export const me = async (user, role) =>
  pmCall(`/pm/me?role=${encodeURIComponent(role)}`, 'GET', null, user);

/**
 * GET /pm/equipment?q=
 * Equipment-/Technisches-Objekt-Suche.
 * @param {Object} user
 * @param {string} q — Suchbegriff
 */
export const searchEquipment = async (user, q, role) =>
  pmCall(`/pm/equipment?q=${encodeURIComponent(q)}`, 'GET', null, user, roleHeader(role));

/**
 * GET /pm/equipment/:id/notifications
 * Fruehere Meldungen an einem Equipment.
 * @param {Object} user
 * @param {string} id — Equipment-ID
 */
export const equipmentNotifications = async (user, id, role) =>
  pmCall(`/pm/equipment/${encodeURIComponent(id)}/notifications`, 'GET', null, user, roleHeader(role));

/**
 * GET /pm/orders?<params>
 * Auftragsliste (Meine Auftraege / Auftragsbestand / Freigaben), gefiltert.
 * @param {Object} user
 * @param {Object} [params] — beliebige Filter (z. B. { status, scope, q }),
 *                            werden via URLSearchParams serialisiert.
 */
export const listOrders = async (user, params = {}, role) => {
  const qs = new URLSearchParams(params);
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return pmCall(`/pm/orders${query}`, 'GET', null, user, roleHeader(role));
};

/**
 * GET /pm/orders/:orderId
 * Auftragsdetail (Arbeitsgaenge, Komponenten, Status).
 * @param {Object} user
 * @param {string} orderId
 */
export const getOrder = async (user, orderId, role) =>
  pmCall(`/pm/orders/${encodeURIComponent(orderId)}`, 'GET', null, user, roleHeader(role));

/**
 * GET /pm/dashboard?plant=
 * Meister-Uebersicht: Kennzahlen + Aufschluesselungen + neueste Meldungen.
 * Jede Kennzahl traegt `source` ('mock'|'sap') — die UI zeigt bei 'mock' das Mock-Badge.
 * @param {Object} user
 * @param {string} plant — aktives Werk (me.activePlant)
 * @param {string} [role]
 */
export const getDashboard = async (user, plant, role) =>
  pmCall(`/pm/dashboard?plant=${encodeURIComponent(plant)}`, 'GET', null, user, roleHeader(role));

/**
 * GET /pm/materials?q=
 * Materialsuche.
 * @param {Object} user
 * @param {string} q — Suchbegriff
 */
export const searchMaterials = async (user, q, role) =>
  pmCall(`/pm/materials?q=${encodeURIComponent(q)}`, 'GET', null, user, roleHeader(role));

/**
 * GET /pm/materials/:id/stock?plant=
 * Bestand eines Materials je Werk.
 * @param {Object} user
 * @param {string} id — Materialnummer
 * @param {string} plant — Werk
 */
export const materialStock = async (user, id, plant, role) =>
  pmCall(`/pm/materials/${encodeURIComponent(id)}/stock?plant=${encodeURIComponent(plant)}`, 'GET', null, user, roleHeader(role));

/**
 * POST /pm/orders  (Header: Idempotency-Key, X-PM-Role)
 * Legt einen Instandhaltungsauftrag in SAP an (ueber den Adapter).
 * Ein Retry mit gleichem Key liefert dasselbe Ergebnis — kein Duplikat.
 * @param {Object} user
 * @param {Object} body — Auftrags-Payload
 * @param {string} idempotencyKey
 * @param {string} [role] — aktive PM-Rolle (me.profile.id) fuer das Server-Gate
 */
export const createOrder = async (user, body, idempotencyKey, role) =>
  pmCall('/pm/orders', 'POST', body, user, writeHeaders(idempotencyKey, role));

/**
 * POST /pm/notifications  (Header: Idempotency-Key, X-PM-Role)
 * Legt eine Meldung in SAP an.
 * @param {Object} user
 * @param {Object} body — Meldungs-Payload
 * @param {string} idempotencyKey
 * @param {string} [role]
 */
export const createNotification = async (user, body, idempotencyKey, role) =>
  pmCall('/pm/notifications', 'POST', body, user, writeHeaders(idempotencyKey, role));

/**
 * POST /pm/orders/:orderId/components  (Header: Idempotency-Key, X-PM-Role)
 * Uebergibt Komponenten an einen Auftrag. Reservierung/BANF erzeugt SAP selbst —
 * die Ergebnisse kommen in result.messages und als Component-Felder am Auftrag zurueck.
 * @param {Object} user
 * @param {string} orderId
 * @param {Array<{materialId: string, requiredQty: number, unit?: string}>} components
 * @param {string} idempotencyKey
 * @param {string} [role]
 */
export const addOrderComponents = async (user, orderId, components, idempotencyKey, role) =>
  pmCall(`/pm/orders/${encodeURIComponent(orderId)}/components`, 'POST', { components }, user, writeHeaders(idempotencyKey, role));

/**
 * POST /pm/orders/:orderId/confirmations  (Header: Idempotency-Key, X-PM-Role)
 * Bucht eine Rueckmeldung (Zeit/Mengen) zu einem Arbeitsgang.
 * @param {Object} user
 * @param {string} orderId
 * @param {Object} body — Rueckmelde-Payload
 * @param {string} idempotencyKey
 * @param {string} [role]
 */
export const confirmOperation = async (user, orderId, body, idempotencyKey, role) =>
  pmCall(`/pm/orders/${encodeURIComponent(orderId)}/confirmations`, 'POST', body, user, writeHeaders(idempotencyKey, role));

/**
 * GET /pm/orders/:orderId/confirmations — what has actually been booked, newest first.
 * Reversals and reversed rows come back flagged rather than filtered, so the screen can strike
 * them through: hiding them would make the hours on screen stop adding up.
 */
export const listConfirmations = async (user, orderId, role) =>
  pmCall(`/pm/orders/${encodeURIComponent(orderId)}/confirmations`, 'GET', null, user, role ? { 'X-PM-Role': role } : {});

/**
 * POST /pm/orders/:orderId/confirmations/:id/:counter/reverse
 * SAP needs the RUECK + RMZHL pair — the confirmation number alone does not identify the row.
 * Reversal posts a counter-confirmation; nothing is deleted.
 */
export const reverseConfirmation = async (user, orderId, confirmationId, counter, text, idempotencyKey, role) =>
  pmCall(
    `/pm/orders/${encodeURIComponent(orderId)}/confirmations/${encodeURIComponent(confirmationId)}/${encodeURIComponent(counter)}/reverse`,
    'POST', text ? { text } : null, user, writeHeaders(idempotencyKey, role),
  );

/**
 * POST /pm/orders/:orderId/release  (Header: Idempotency-Key, X-PM-Role, kein Body)
 * Gibt einen Auftrag frei.
 * @param {Object} user
 * @param {string} orderId
 * @param {string} idempotencyKey
 * @param {string} [role]
 */
export const releaseOrder = async (user, orderId, idempotencyKey, role) =>
  pmCall(`/pm/orders/${encodeURIComponent(orderId)}/release`, 'POST', null, user, writeHeaders(idempotencyKey, role));

/**
 * POST /pm/orders/:orderId/teco  (Header: Idempotency-Key, X-PM-Role, kein Body)
 * Technischer Abschluss: die Arbeit ist fertig. Recht canTechnicallyComplete (Meister).
 * @param {Object} user @param {string} orderId @param {string} idempotencyKey @param {string} [role]
 */
export const tecoOrder = async (user, orderId, idempotencyKey, role) =>
  pmCall(`/pm/orders/${encodeURIComponent(orderId)}/teco`, 'POST', null, user, writeHeaders(idempotencyKey, role));

/**
 * POST /pm/orders/:orderId/close  (Header: Idempotency-Key, X-PM-Role, kein Body)
 * Kaufmaennischer Abschluss: danach ist KEINE Buchung mehr moeglich. Recht canBusinessComplete
 * (standardmaessig nur Controlling). SAP verlangt vorher TECO und endrueckgemeldete Vorgaenge.
 * @param {Object} user @param {string} orderId @param {string} idempotencyKey @param {string} [role]
 */
export const closeOrder = async (user, orderId, idempotencyKey, role) =>
  pmCall(`/pm/orders/${encodeURIComponent(orderId)}/close`, 'POST', null, user, writeHeaders(idempotencyKey, role));

/**
 * POST /pm/voice/transcribe?lang=  (Body: rohes Audio-Blob, z. B. audio/webm)
 * Diktat ueber den unternehmenseigenen Sprach-Server (Backend-Proxy; nur wenn der
 * Verbindungstest bestanden ist). Eigener fetch statt pmCall: binaerer Body statt JSON.
 * @param {Object} user
 * @param {Blob} audioBlob
 * @param {string} lang — 'de' | 'en'
 * @param {string} [role]
 */
export const transcribeAudio = async (user, audioBlob, lang, role) => {
  const headers = { 'Content-Type': audioBlob.type || 'audio/webm', ...roleHeader(role) };
  if (user?.token && !user.token.startsWith('mock-')) {
    headers['Authorization'] = `Bearer ${user.token}`;
  }
  const response = await fetch(`/api/pm/voice/transcribe?lang=${encodeURIComponent(lang || 'de')}`, {
    method: 'POST', headers, body: audioBlob,
  });
  if (!response.ok) {
    let body = null;
    try { body = await response.json(); } catch { /* kein JSON */ }
    const err = new Error(body?.error || `HTTP error! status: ${response.status}`);
    err.status = response.status;
    throw err;
  }
  return response.json();
};

/**
 * GET/PUT /user-preferences/:key — generische, userbezogene Persistenz (Core-Route, UX-3).
 * PM nutzt sie fuer die Zuletzt-verwendet-Listen (recency.js). Key-Muster: [a-z0-9_-]{1,60}.
 * @param {Object} user
 * @param {string} key
 */
export const getPreference = async (user, key) =>
  pmCall(`/user-preferences/${encodeURIComponent(key)}`, 'GET', null, user);

/** @param {Object} user @param {string} key @param {any} value */
export const setPreference = async (user, key, value) =>
  pmCall(`/user-preferences/${encodeURIComponent(key)}`, 'PUT', { value }, user);

// ── Vorlagen (portal-eigen, Slice a) ──

/** GET /pm/templates?plant= — eigene + geteilte Vorlagen, meistgenutzte zuerst. */
export const listTemplates = async (user, plant, role) =>
  pmCall(`/pm/templates${plant ? `?plant=${encodeURIComponent(plant)}` : ''}`, 'GET', null, user, roleHeader(role));

/** POST /pm/templates — ausgefuellten Wizard als Vorlage speichern. */
export const saveTemplate = async (user, body, role) =>
  pmCall('/pm/templates', 'POST', body, user, roleHeader(role));

/** POST /pm/templates/:id/use — Nutzung zaehlen (Sortier-Signal; fire-and-forget). */
export const useTemplate = async (user, id, role) =>
  pmCall(`/pm/templates/${encodeURIComponent(id)}/use`, 'POST', null, user, roleHeader(role));

/** DELETE /pm/templates/:id — eigene Vorlage (canManageUsers: jede) loeschen. */
export const deleteTemplate = async (user, id, role) =>
  pmCall(`/pm/templates/${encodeURIComponent(id)}`, 'DELETE', null, user, roleHeader(role));

// ── PM-Einstellungen (Admin; Rechte-Gate canManageUsers serverseitig) ──

/** GET /pm/admin/order-types — Kunden-Mapping SAP-Auftragsart -> Anzeige. */
export const getOrderTypesAdmin = async (user) =>
  pmCall('/pm/admin/order-types', 'GET', null, user);

/** PUT /pm/admin/order-types — Mapping komplett ersetzen. */
export const saveOrderTypesAdmin = async (user, orderTypes) =>
  pmCall('/pm/admin/order-types', 'PUT', { orderTypes }, user);

/** PUT /pm/admin/features — PM-Feature-Flags (z. B. voice). */
export const savePmFeatures = async (user, flags) =>
  pmCall('/pm/admin/features', 'PUT', flags, user);

/** PUT /pm/admin/voice-server — Sprach-Server setzen + live verifizieren. */
export const savePmVoiceServer = async (user, body) =>
  pmCall('/pm/admin/voice-server', 'PUT', body, user);

/** GET /pm/admin/scope — instance scope: plants/work centers/order types of THIS SAP system. */
export const getScopeAdmin = async (user) =>
  pmCall('/pm/admin/scope', 'GET', null, user);

/** PUT /pm/admin/scope — replace the scope completely (the lists ARE the configuration). */
export const saveScopeAdmin = async (user, scope) =>
  pmCall('/pm/admin/scope', 'PUT', { scope }, user);

/**
 * GET /pm/admin/sap-discovery[?plant=] — ask the SAP system for its own configuration
 * (ABAP op=discover). With plant additionally: order types incl. readiness checklist,
 * work centers, planner groups, equipment counts. The mock adapter answers 501.
 */
export const getSapDiscovery = async (user, plant) =>
  pmCall(`/pm/admin/sap-discovery${plant ? `?plant=${encodeURIComponent(plant)}` : ''}`, 'GET', null, user);

/**
 * POST /pm/admin/reset  (nur Mock-Adapter; Header: X-PM-Role)
 * Demo-Only: setzt angelegte/persistierte Daten auf den Fixture-Ausgangsstand zurueck.
 * Serverseitig auf canManageUsers gegatet (Meister-Profil) — daher Rolle mitschicken.
 * @param {Object} user
 * @param {string} [role]
 */
export const resetData = async (user, role) =>
  pmCall('/pm/admin/reset', 'POST', null, user, roleHeader(role));

// Gebuendelter Default-Export, damit Screens wahlweise `import { listOrders }`
// ODER `import api from '.../api'` (-> api.listOrders) nutzen koennen.
// ── Process endpoints (2026-08-09 expansion: notifications lifecycle, equipment, preventive
//    setup, measurements, goods issue, costs). Same conventions as above: user first, role
//    header on reads too, Idempotency-Key on writes. ──

/** List notifications (plant/equipment filter server-side, scope-filtered). */
export const listNotifications = async (user, params = {}, role) => {
  const qs = new URLSearchParams(params);
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return pmCall(`/pm/notifications${query}`, 'GET', null, user, roleHeader(role));
};

/** Notification detail (404 -> throws with .status). */
export const getNotification = async (user, id, role) =>
  pmCall(`/pm/notifications/${encodeURIComponent(id)}`, 'GET', null, user, roleHeader(role));

/** OSNO -> NOPR: mechanic starts working on the report. */
export const notificationInProgress = async (user, id, idempotencyKey, role) =>
  pmCall(`/pm/notifications/${encodeURIComponent(id)}/in-progress`, 'POST', null, user, writeHeaders(idempotencyKey, role));

/** NOPR -> NOCO: supervisor completes the report. body.malfunctionEnd (ISO) feeds MTTR. */
export const closeNotification = async (user, id, idempotencyKey, role, body = null) =>
  pmCall(`/pm/notifications/${encodeURIComponent(id)}/close`, 'POST', body, user, writeHeaders(idempotencyKey, role));

/** IW34: create an order FOR the notification (defaults come from the notification). */
export const createOrderForNotification = async (user, id, body, idempotencyKey, role) =>
  pmCall(`/pm/notifications/${encodeURIComponent(id)}/order`, 'POST', body, user, writeHeaders(idempotencyKey, role));

/** IE01: create equipment ({ description, plant, costCenter?, plannerGroup?, location? }). */
export const createEquipment = async (user, body, idempotencyKey, role) =>
  pmCall('/pm/equipment', 'POST', body, user, writeHeaders(idempotencyKey, role));

/** IE02: change equipment (only the sent fields change). */
export const changeEquipment = async (user, id, body, idempotencyKey, role) =>
  pmCall(`/pm/equipment/${encodeURIComponent(id)}`, 'PUT', body, user, writeHeaders(idempotencyKey, role));

/** Measuring points of an equipment. */
export const listMeasuringPoints = async (user, equipmentId, role) =>
  pmCall(`/pm/equipment/${encodeURIComponent(equipmentId)}/measuring-points`, 'GET', null, user, roleHeader(role));

/** IK11: record a reading ({ point, value, unit?, text?, readingDate?, reader?, isDifference? }). */
export const createMeasurementDoc = async (user, body, idempotencyKey, role) =>
  pmCall('/pm/measurement-docs', 'POST', body, user, writeHeaders(idempotencyKey, role));

// ── counter-based maintenance (2026-08-27) ──
// All of these answer 501 when the counter adapter class is not deployed on the customer's SAP
// system; the panel shows that message instead of an empty list.

/** Measuring points of an equipment WITH their current reading (`countersOnly` drops sensors). */
export const listCounters = async (user, equipmentId, countersOnly, role) =>
  pmCall(`/pm/equipment/${encodeURIComponent(equipmentId)}/counters${countersOnly ? '?countersOnly=1' : ''}`,
    'GET', null, user, roleHeader(role));

/** One measuring point in detail, incl. the plans that schedule on it. */
export const getMeasuringPoint = async (user, point, role) =>
  pmCall(`/pm/measuring-points/${encodeURIComponent(point)}`, 'GET', null, user, roleHeader(role));

/** Reading history of a point (newest first; cancelled documents included and flagged). */
export const listReadings = async (user, point, limit, role) =>
  pmCall(`/pm/measuring-points/${encodeURIComponent(point)}/readings${limit ? `?limit=${limit}` : ''}`,
    'GET', null, user, roleHeader(role));

/** IK12: reverse ONE reading. Idempotent — reversing twice is not an error. */
export const cancelReading = async (user, measurementDocId, idempotencyKey, role) =>
  pmCall(`/pm/measurement-docs/${encodeURIComponent(measurementDocId)}/cancel`, 'POST', null, user,
    writeHeaders(idempotencyKey, role));

/** Performance-based / multiple-counter plan ({ description, plant, taskListGroup, cycles: [...] }). */
export const createCounterPlan = async (user, body, idempotencyKey, role) =>
  pmCall('/pm/plans/counter', 'POST', body, user, writeHeaders(idempotencyKey, role));

/** Per-cycle "how far from the next service, and roughly when". */
export const getPlanCounterStatus = async (user, planId, role) =>
  pmCall(`/pm/plans/${encodeURIComponent(planId)}/counter-status`, 'GET', null, user, roleHeader(role));

/** Task lists of a plant. */
export const listTaskLists = async (user, plant, role) =>
  pmCall(`/pm/tasklists${plant ? `?plant=${encodeURIComponent(plant)}` : ''}`, 'GET', null, user, roleHeader(role));

/** IA05: create a task list ({ description, plant, validFrom?, operations: [...] }). */
export const createTaskList = async (user, body, idempotencyKey, role) =>
  pmCall('/pm/tasklists', 'POST', body, user, writeHeaders(idempotencyKey, role));

/** Maintenance plans of a plant. */
export const listPlans = async (user, plant, role) =>
  pmCall(`/pm/plans${plant ? `?plant=${encodeURIComponent(plant)}` : ''}`, 'GET', null, user, roleHeader(role));

/** Plan detail incl. items, cycles and the newest calls (with generated orders). */
export const getPlan = async (user, id, role) =>
  pmCall(`/pm/plans/${encodeURIComponent(id)}`, 'GET', null, user, roleHeader(role));

/** IP41: create a plan ({ description, plant, cycleDays, taskListGroup, startDate?, ... }). */
export const createPlan = async (user, body, idempotencyKey, role) =>
  pmCall('/pm/plans', 'POST', body, user, writeHeaders(idempotencyKey, role));

/** IP10/IP30: schedule the plan now (runs a background job SAP-side — allow a few seconds). */
export const schedulePlan = async (user, id, idempotencyKey, role) =>
  pmCall(`/pm/plans/${encodeURIComponent(id)}/schedule`, 'POST', null, user, writeHeaders(idempotencyKey, role));

/** IP42 activate/deactivate. */
export const setPlanActive = async (user, id, active, idempotencyKey, role) =>
  pmCall(`/pm/plans/${encodeURIComponent(id)}/${active ? 'activate' : 'deactivate'}`, 'POST', null, user, writeHeaders(idempotencyKey, role));

/** MIGO 261: unplanned goods issue to the order. */
export const postGoodsIssue = async (user, orderId, body, idempotencyKey, role) =>
  pmCall(`/pm/orders/${encodeURIComponent(orderId)}/goods-issue`, 'POST', body, user, writeHeaders(idempotencyKey, role));

/** Order costs (planned vs actual, PMCO) — needs canViewCosts. */
export const getOrderCosts = async (user, orderId, role) =>
  pmCall(`/pm/orders/${encodeURIComponent(orderId)}/costs`, 'GET', null, user, roleHeader(role));

/** Reports R1-R5 (server-cached ~10 min). params: { plant, year?, days?, equipmentId? }. */
const reportCall = (name) => async (user, reportParams = {}, role) => {
  const qs = new URLSearchParams(Object.entries(reportParams).filter(([, v]) => v != null && v !== ''));
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return pmCall(`/pm/reports/${name}${query}`, 'GET', null, user, roleHeader(role));
};
export const getReportCosts = reportCall('costs');
export const getReportBreakdowns = reportCall('breakdowns');
export const getReportCompliance = reportCall('compliance');
export const getReportHours = reportCall('hours');
export const getReportParts = reportCall('parts');
export const getReportDamages = reportCall('damages');

/** Damage/cause code value help for the notification wizard. */
export const getNotifCatalogs = async (user, type, role) =>
  pmCall(`/pm/notif-catalogs${type ? `?type=${encodeURIComponent(type)}` : ''}`, 'GET', null, user, roleHeader(role));

/** Assign/unassign a person to an order (portal-owned; empty username unassigns). */
export const setOrderAssignee = async (user, orderId, username, role) =>
  pmCall(`/pm/orders/${encodeURIComponent(orderId)}/assignee`, 'PUT', { username }, user, roleHeader(role));

/** Order ids assigned to me (merged into "My orders"). */
export const myAssignments = async (user, role) =>
  pmCall('/pm/assignments/mine', 'GET', null, user, roleHeader(role));

/** Combobox data for the assignee field: recent assignees + all PM portal users. */
export const getAssigneeSuggest = async (user, role) =>
  pmCall('/pm/assignees/suggest', 'GET', null, user, roleHeader(role));

/** Kiosk admin config (canManageUsers): read carries the token for label printing. */
export const getKioskAdmin = async (user) =>
  pmCall('/pm/admin/kiosk', 'GET', null, user);

// ── Personal SAP account (PM-USER-ATTRIBUTION.md Layer C): self-service credentials; PM writes
//    then run in SAP under this user's own name. GET returns metadata only, never the password. ──
export const getSapAccount = async (user) =>
  pmCall('/pm/me/sap-account', 'GET', null, user);

export const saveSapAccount = async (user, sapUsername, password) =>
  pmCall('/pm/me/sap-account', 'PUT', { sapUsername, password }, user);

export const deleteSapAccount = async (user) =>
  pmCall('/pm/me/sap-account', 'DELETE', null, user);

// ── Notification photos via the shared files module (portal-side storage; SAP never sees
//    them — the contract's photoRefs). entity_type pm_notification, entity_id = qmnum. ──

/** Server-side cap for category 'image' (backend/core/services/fileStorage.js MAX_BYTES.image).
 *  Mirrored here so a screen can refuse an over-sized photo BEFORE the upload: a current phone
 *  camera JPEG routinely exceeds it and the 413 arrives when the technician has already walked
 *  away from the machine (audit finding 05). Keep in step with fileStorage.js. */
export const PHOTO_MAX_BYTES = 5 * 1024 * 1024;

/** GET /api/files answers a BARE ARRAY (backend/routes/files.js:158), never `{files}`. Both
 *  branches therefore return an array: the old success branch handed the array to callers that
 *  read `res.files`, so the thumbnail list was always empty (audit finding 05). */
export const listNotificationPhotos = async (user, qmnum) => {
  const res = await fetch(`/api/files?entity_type=pm_notification&entity_id=${encodeURIComponent(qmnum)}`, {
    headers: user?.token && !user.token.startsWith('mock-') ? { Authorization: `Bearer ${user.token}` } : {},
  });
  if (!res.ok) return [];
  const body = await res.json().catch(() => []);
  return Array.isArray(body) ? body : (body.files || []);
};
/** Throws on failure; `err.tooLarge` marks the pre-flight case so the screen can name the cap. */
export const uploadNotificationPhoto = async (user, qmnum, file, plant) => {
  if (file && file.size > PHOTO_MAX_BYTES) {
    const err = new Error(`photo too large (${file.size} > ${PHOTO_MAX_BYTES})`);
    err.tooLarge = true;
    throw err;
  }
  const fd = new FormData();
  fd.append('entity_type', 'pm_notification');
  fd.append('entity_id', String(qmnum));
  fd.append('category', 'image');
  // The plant is recorded ON the file row so the photo can be scope-checked later without asking
  // SAP per thumbnail (migration 2026-09-03_files_plant.sql). The server re-checks it against the
  // uploader's own PM scope, so naming a foreign plant is refused rather than trusted.
  if (plant) fd.append('plant', String(plant));
  fd.append('file', file);
  const res = await fetch('/api/files', {
    method: 'POST',
    headers: user?.token && !user.token.startsWith('mock-') ? { Authorization: `Bearer ${user.token}` } : {},
    body: fd,
  });
  if (!res.ok) throw new Error(`upload failed (${res.status})`);
  return res.json();
};

const api = {
  me,
  searchEquipment,
  equipmentNotifications,
  getDashboard,
  listOrders,
  getOrder,
  searchMaterials,
  materialStock,
  createOrder,
  createNotification,
  addOrderComponents,
  confirmOperation,
  listConfirmations,
  reverseConfirmation,
  releaseOrder,
  tecoOrder,
  closeOrder,
  listTemplates,
  saveTemplate,
  useTemplate,
  deleteTemplate,
  getOrderTypesAdmin,
  saveOrderTypesAdmin,
  savePmFeatures,
  savePmVoiceServer,
  getScopeAdmin,
  saveScopeAdmin,
  getSapDiscovery,
  resetData,
  listNotifications,
  getNotification,
  notificationInProgress,
  closeNotification,
  createOrderForNotification,
  createEquipment,
  changeEquipment,
  listMeasuringPoints,
  createMeasurementDoc,
  listCounters,
  getMeasuringPoint,
  listReadings,
  cancelReading,
  createCounterPlan,
  getPlanCounterStatus,
  listTaskLists,
  createTaskList,
  listPlans,
  getPlan,
  createPlan,
  schedulePlan,
  setPlanActive,
  postGoodsIssue,
  getOrderCosts,
  getReportCosts,
  getReportBreakdowns,
  getReportCompliance,
  getReportHours,
  getReportParts,
  getReportDamages,
  getNotifCatalogs,
  setOrderAssignee,
  myAssignments,
  getAssigneeSuggest,
  getKioskAdmin,
  listNotificationPhotos,
  uploadNotificationPhoto,
  getSapAccount,
  saveSapAccount,
  deleteSapAccount,
};

export default api;
